import React, { useRef, useState } from "react";
import { ArrowDown, ArrowUp, Copy, ImagePlus, Plus, Trash2, X } from "lucide-react";
import { ACCEPTED_IMAGES, uploadImage } from "./assets";
import { BLOCK_TYPES, blockTypesFor, layoutsFor, newBlock } from "./proposalOps";
import TwinPicker from "./TwinPicker";

const TAGS = ["EXEMPLE ILLUSTRATIF", "PRINCIPE ENVISAGÉ", "ILLUSTRATION NON CONTRACTUELLE", "À ARRÊTER À L'ITÉRATION 1"];

const Section = ({ title, children }) => (
  <section className="space-y-2">
    <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</h3>
    {children}
  </section>
);

const Field = ({ label, children }) => (
  <label className="block text-xs font-medium text-slate-600">
    {label}
    <div className="mt-1">{children}</div>
  </label>
);

const input = "w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 focus:border-blue-400 focus:outline-none";

const IconButton = ({ title, onClick, children, danger, disabled }) => (
  <button type="button" title={title} aria-label={title} onClick={onClick} disabled={disabled}
    className={`rounded p-1.5 disabled:opacity-30 ${danger ? "text-slate-500 hover:bg-red-50 hover:text-red-600" : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"}`}>
    {children}
  </button>
);

// Choix et envoi d'une image ; renvoie la référence « asset:ID ».
export const ImagePicker = ({ value, onChange, label = "Image" }) => {
  const ref = useRef(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const pick = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      onChange(await uploadImage(file));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => ref.current?.click()} disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1 text-xs text-slate-700 hover:bg-slate-50">
          <ImagePlus className="h-3.5 w-3.5" /> {busy ? "Envoi…" : value ? `Remplacer ${label.toLowerCase()}` : `Ajouter ${label.toLowerCase()}`}
        </button>
        {value && (
          <button type="button" onClick={() => onChange(null)} className="text-xs text-slate-500 hover:text-red-600">Retirer</button>
        )}
      </div>
      <input ref={ref} type="file" accept={ACCEPTED_IMAGES.join(",")} className="hidden" onChange={pick} aria-label={label} />
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
};

const LotSelect = ({ value, lots, onChange, allowNone }) => (
  <select className={input} value={value || ""} onChange={(event) => onChange(event.target.value || null)}>
    {allowNone && <option value="">Aucun prix</option>}
    {!allowNone && !lots.some((lot) => lot.id === value) && <option value={value || ""}>Lot introuvable</option>}
    {lots.map((lot, index) => <option key={lot.id} value={lot.id}>Lot {index + 1} · {lot.name}</option>)}
  </select>
);

// Prix unitaire des lots chiffrés en quantité (ex. 4 boîtiers), affiché par défaut.
const UnitPriceOption = ({ block, set }) => (
  <label className="flex items-center gap-2 text-sm text-slate-700">
    <input type="checkbox" checked={block.unit_price !== false} onChange={(event) => set({ unit_price: event.target.checked })} />
    Prix unitaire des lots en quantité
  </label>
);

const BlockSettings = ({ block, lots, onBlock }) => {
  const set = (patch) => onBlock(patch);
  switch (block.type) {
    case "text":
      return (
        <div className="space-y-3">
          <Field label="Style">
            <select className={input} value={block.style} onChange={(event) => set({ style: event.target.value })}>
              <option value="plain">Texte courant</option>
              <option value="box">Encart bleu pâle</option>
              <option value="note">Mention grise</option>
            </select>
          </Field>
          <div className="space-y-2 rounded-lg border border-slate-200 p-2">
            <span className="text-xs font-semibold text-slate-600">Image à côté du texte</span>
            <ImagePicker value={block.image} onChange={(ref) => set({ image: ref })} />
            {block.image && (
              <>
                <Field label="Côté">
                  <select className={input} value={block.image_side || "right"}
                    onChange={(event) => set({ image_side: event.target.value })}>
                    <option value="right">À droite</option>
                    <option value="left">À gauche</option>
                  </select>
                </Field>
                <Field label={`Largeur : ${block.image_width || 240} px`}>
                  <input type="range" min={120} max={520} step={10} value={block.image_width || 240} className="w-full"
                    onChange={(event) => set({ image_width: Number(event.target.value) })} />
                </Field>
              </>
            )}
          </div>
        </div>
      );
    case "columns":
      return (
        <div className="space-y-3">
          <Field label="Style">
            <select className={input} value={block.style} onChange={(event) => set({ style: event.target.value })}>
              <option value="plain">Colonnes de texte</option>
              <option value="cards">Cartes (frise d'étapes)</option>
            </select>
          </Field>
          {block.style === "cards" && (
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={Boolean(block.arrows)} onChange={(event) => set({ arrows: event.target.checked })} />
              Flèches entre les cartes
            </label>
          )}
          {block.items.map((item, index) => (
            <div key={index} className="space-y-2 rounded-lg border border-slate-200 p-2">
              <div className="flex items-center justify-between text-xs font-semibold text-slate-600">
                Colonne {index + 1}
                <IconButton title="Retirer la colonne" danger disabled={block.items.length < 2}
                  onClick={() => set({ items: block.items.filter((_, position) => position !== index) })}>
                  <X className="h-3.5 w-3.5" />
                </IconButton>
              </div>
              <Field label="Prix d'un lot sous la colonne">
                <LotSelect value={item.lot_id} lots={lots} allowNone
                  onChange={(lotId) => set({ items: block.items.map((it, p) => (p === index ? { ...it, lot_id: lotId } : it)) })} />
              </Field>
              <ImagePicker value={item.image}
                onChange={(ref) => set({ items: block.items.map((it, p) => (p === index ? { ...it, image: ref } : it)) })} />
            </div>
          ))}
          {block.items.length < 4 && (
            <button type="button" onClick={() => set({ items: [...block.items, { label: "", text: "" }] })}
              className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900">
              <Plus className="h-3.5 w-3.5" /> Ajouter une colonne
            </button>
          )}
        </div>
      );
    case "image":
      return (
        <div className="space-y-3">
          <ImagePicker value={block.image} onChange={(ref) => set({ image: ref })} />
          <Field label={`Hauteur : ${block.height} px`}>
            <input type="range" min={80} max={480} step={10} value={block.height} className="w-full"
              onChange={(event) => set({ height: Number(event.target.value) })} />
          </Field>
          <p className="text-xs text-slate-500">Sans image, la zone reste réservée et la légende sert de brief.</p>
        </div>
      );
    case "twin":
      return (
        <div className="space-y-3">
          {/* Autre jumeau : l'image fixe choisie pour l'ancien ne vaut plus */}
          <TwinPicker value={block.embed} onChange={(ref) => set(ref === block.embed ? { embed: ref } : { embed: ref, poster: null })} />
          <Field label={`Hauteur : ${block.height} px`}>
            <input type="range" min={160} max={495} step={5} value={block.height} className="w-full"
              onChange={(event) => set({ height: Number(event.target.value) })} />
          </Field>
          <div className="space-y-1 rounded-lg border border-slate-200 p-2">
            <span className="text-xs font-semibold text-slate-600">Image fixe (PDF et miniatures)</span>
            <ImagePicker value={block.poster} label="Image" onChange={(ref) => set({ poster: ref })} />
            <p className="text-xs text-slate-500">Sans image choisie : la vue capturée par le serveur à l'ajout du jumeau.</p>
          </div>
          <p className="text-xs text-slate-500">Le jumeau se manipule sur la slide ouverte et en présentation.</p>
        </div>
      );
    case "lot_price":
      return (
        <div className="space-y-2">
          <Field label="Lot">
            <LotSelect value={block.lot_id} lots={lots} onChange={(lotId) => set({ lot_id: lotId })} />
          </Field>
          <UnitPriceOption block={block} set={set} />
        </div>
      );
    case "quote": {
      const selected = block.lot_ids || lots.map((lot) => lot.id);
      return (
        <div className="space-y-2">
          <span className="text-xs font-medium text-slate-600">Lots du devis</span>
          {lots.map((lot, index) => (
            <label key={lot.id} className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={selected.includes(lot.id)} onChange={(event) => {
                const next = event.target.checked ? lots.map((l) => l.id).filter((id) => id === lot.id || selected.includes(id))
                  : selected.filter((id) => id !== lot.id);
                set({ lot_ids: next.length === lots.length ? null : next });
              }} />
              Lot {index + 1} · {lot.name}
            </label>
          ))}
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={Boolean(block.show_total)} onChange={(event) => set({ show_total: event.target.checked })} />
            Ligne de total (hors options)
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={block.lot_prefix !== false} onChange={(event) => set({ lot_prefix: event.target.checked })} />
            « Lot N · » devant chaque ligne
          </label>
          <UnitPriceOption block={block} set={set} />
        </div>
      );
    }
    case "table": {
      const columns = block.rows[0].length;
      return (
        <div className="flex flex-wrap gap-2 text-xs">
          <button type="button" className="rounded-full border border-slate-300 px-2 py-1" disabled={block.rows.length >= 20}
            onClick={() => set({ rows: [...block.rows, Array(columns).fill("")] })}>+ ligne</button>
          <button type="button" className="rounded-full border border-slate-300 px-2 py-1" disabled={block.rows.length <= 1}
            onClick={() => set({ rows: block.rows.slice(0, -1) })}>− ligne</button>
          <button type="button" className="rounded-full border border-slate-300 px-2 py-1" disabled={columns >= 6}
            onClick={() => set({ rows: block.rows.map((row) => [...row, ""]) })}>+ colonne</button>
          <button type="button" className="rounded-full border border-slate-300 px-2 py-1" disabled={columns <= 1}
            onClick={() => set({ rows: block.rows.map((row) => row.slice(0, -1)) })}>− colonne</button>
        </div>
      );
    }
    default:
      return null;
  }
};

const SlideInspector = ({
  slide, block, lots, editable, onSlide, onBlock, onAddBlock, onMoveBlock, onDuplicateBlock, onRemoveBlock, onDeselect,
  document = "proposition",
}) => {
  if (!slide) return null;
  if (block) {
    const label = BLOCK_TYPES.find((item) => item.type === block.type)?.label || block.type;
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <button type="button" onClick={onDeselect} className="text-xs text-slate-500 hover:text-slate-800">← Slide</button>
          <div className="flex">
            <IconButton title="Monter le bloc" onClick={() => onMoveBlock(-1)} disabled={!editable}><ArrowUp className="h-4 w-4" /></IconButton>
            <IconButton title="Descendre le bloc" onClick={() => onMoveBlock(1)} disabled={!editable}><ArrowDown className="h-4 w-4" /></IconButton>
            <IconButton title="Dupliquer le bloc" onClick={onDuplicateBlock} disabled={!editable}><Copy className="h-4 w-4" /></IconButton>
            <IconButton title="Supprimer le bloc" danger onClick={onRemoveBlock} disabled={!editable}><Trash2 className="h-4 w-4" /></IconButton>
          </div>
        </div>
        <Section title={label}>
          {editable ? <BlockSettings block={block} lots={lots} onBlock={onBlock} /> : <p className="text-xs text-slate-500">Lecture seule.</p>}
        </Section>
        <p className="text-xs text-slate-500">Les textes se modifient directement dans la slide.</p>
      </div>
    );
  }
  return (
    <div className="space-y-5">
      <Section title="Slide">
        <Field label="Mise en page">
          <select className={input} disabled={!editable} value={slide.layout} onChange={(event) => onSlide({ layout: event.target.value })}>
            {layoutsFor(document).map((item) => <option key={item.layout} value={item.layout}>{item.label}</option>)}
          </select>
        </Field>
        {slide.layout === "visual" && (
          <div className="space-y-2 rounded-lg border border-slate-200 p-2">
            <span className="text-xs font-semibold text-slate-600">Illustration</span>
            {editable && (
              <ImagePicker value={slide.visual?.image} label="Illustration"
                onChange={(ref) => onSlide({ visual: { caption: "", ...(slide.visual || {}), image: ref } })} />
            )}
            <Field label="Côté">
              <select className={input} disabled={!editable} value={slide.visual_side || "right"}
                onChange={(event) => onSlide({ visual_side: event.target.value })}>
                <option value="right">À droite</option>
                <option value="left">À gauche</option>
              </select>
            </Field>
            <p className="text-xs text-slate-500">Sans image, la zone reste réservée et sa légende sert de brief.</p>
            {editable && (
              <div className="space-y-1 border-t border-slate-100 pt-2">
                <span className="text-xs font-semibold text-slate-600">Ou un jumeau 3D (l'image devient son image fixe)</span>
                <TwinPicker value={slide.visual?.embed}
                  onChange={(ref) => onSlide({ visual: { caption: "", image: null, ...(slide.visual || {}), embed: ref } })} />
              </div>
            )}
          </div>
        )}
        {["content", "visual"].includes(slide.layout) && (
          <Field label="Étiquette au-dessus du titre (ce qui n'est pas arrêté)">
            <input className={input} disabled={!editable} list="offer-tags" value={slide.tag || ""} maxLength={60}
              onChange={(event) => onSlide({ tag: event.target.value.toUpperCase() }, "tag")} placeholder="Aucune" />
            <datalist id="offer-tags">{TAGS.map((tag) => <option key={tag} value={tag} />)}</datalist>
          </Field>
        )}
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" disabled={!editable} checked={Boolean(slide.hidden)} onChange={(event) => onSlide({ hidden: event.target.checked })} />
          Masquer (ni affichée ni exportée)
        </label>
      </Section>
      {["content", "visual"].includes(slide.layout) && editable && (
        <Section title="Ajouter un bloc">
          <div className="grid grid-cols-1 gap-1">
            {blockTypesFor(document).map((item) => (
              <button key={item.type} type="button" onClick={() => onAddBlock(newBlock(item.type, lots))}
                className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-100">
                <Plus className="h-3.5 w-3.5 text-slate-400" /> {item.label}
              </button>
            ))}
          </div>
        </Section>
      )}
      {["content", "visual"].includes(slide.layout) && (
        <p className="text-xs text-slate-500">
          Clique un bloc pour le régler ; clique un texte pour l'écrire. Une ligne « - … » devient une puce, **…** met en gras.
        </p>
      )}
    </div>
  );
};

export default SlideInspector;
